import React, { useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { IconButton, Tooltip } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import UncontrolledTextInputM from "@common/components/input/UncontrolledTextInputM"
import { ModalM } from "@common/components/modal/ModalM"
import { IconImageM } from "@common/components/icons/IconsM"
import { theme } from "@common/theme/theme"
import {
    materialLevelsColors,
    materialLevelsIcons,
    materialLevelsNames,
} from "@common/chapters/ActivityLevelBadge"
import {
    getDefaultMaterialDuration,
    MATERIAL_DURATION_OPTIONS,
} from "@common/chapters/materialDuration"
import { SelectInputM } from "../../../account/onboarding/SelectInputM"
import GenerateActivityModal from "../../../../tutor/students/GenerateActivityModal"
import ChapterSearchDropdown from "@common/chapters/ChapterSearchDropdown"
import { hasRole } from "@data/utility/ability"
import { useSourceChapterFix } from "../../../../ports/primary/ui/hooks/useSourceChapterFix"
import {
    SOURCE_CHAPTER_FIX_TEXTS,
    sourceChapterFixNotice,
} from "../../../../ports/primary/view-models/tutor/Students/sourceChapterFix"
import { generationScopeLabel } from "../../../../ports/primary/adapters/parent/Activities/activityGenerationAdapter"

const SOURCE_TITLE_MAX = 40

const truncateSourceTitle = (title) => {
    if (!title) return null
    return title.length > SOURCE_TITLE_MAX
        ? title.slice(0, SOURCE_TITLE_MAX - 1).trimEnd() + "…"
        : title
}

const ParcoursChoiceModal = ({
    visible,
    onDismiss,
    studentId,
    student,
    user,
    materialId,
    sourceLevel,
    sourceTitle,
    // A source whose chapter came from a non-high-confidence scan
    // may be mistagged (wrong matière/chapitre): a tutor picks its chapter
    // right here (`useSourceChapterFix`), the generation waits for it.
    sourceConfidence,
    // The material's subject and class: the chapters offered are the
    // subject's, in the student's class (the material's when unknown).
    sourceSubjectId,
    sourceClass,
    schoolSubjects,
    apiRequest,
    isLiveMode,
    navigation,
}) => {
    // Only a tutor can actually act on "corrige le chapitre" — updateChapter/
    // completeManual are gated by manage_live_sessions. Today the "Générer..."
    // button that opens this modal is itself hasRole(tutor)-gated with no
    // "|| isParentMode" fallback (TodoModal.js), so a student/parent can't
    // reach this screen in the first place — but that's a property of the
    // caller, not of this component. Branching here too means the copy stays
    // correct even if that upstream gate ever changes.
    const canFixChapter = hasRole(user, "tutor")
    const [isSimilarOpen, setSimilarOpen] = useState(false)
    const [similarPersonalize, setSimilarPersonalize] = useState(false)
    const [similarInstructions, setSimilarInstructions] = useState("")

    const resolvedStudent = user?.students?.find((x) => x?.id === studentId) ?? student
    const fixClass = resolvedStudent?.profile?.class ?? sourceClass ?? null
    const fix = useSourceChapterFix({
        source:
            materialId != null
                ? { materialId, confidence: sourceConfidence ?? null, subjectId: sourceSubjectId ?? null }
                : null,
        studentId: resolvedStudent?.id ?? studentId ?? null,
        className: fixClass,
        active: !!visible && canFixChapter,
    })
    const sourceUnreliable = fix.unreliable
    // Nothing to pick (a tutor's list came back empty): the tutor generates
    // without a chapter, the material confirmed as is.
    const fixWithoutChapter = fix.status === "empty"
    const generateLocked = fix.saving || (sourceUnreliable && !fixWithoutChapter)
    const fixScope = generationScopeLabel({ className: fixClass, subjectId: sourceSubjectId }, schoolSubjects)
    const defaultLevel = sourceLevel ? String(sourceLevel) : "2"
    const defaultDuration = getDefaultMaterialDuration(resolvedStudent?.profile?.class)

    const [selectedLevel, setSelectedLevel] = useState(defaultLevel)
    const [selectedDuration, setSelectedDuration] = useState(defaultDuration)

    // Re-align presets when the modal is re-opened with a different source material
    React.useEffect(() => {
        if (visible) {
            setSelectedLevel(sourceLevel ? String(sourceLevel) : "2")
            setSelectedDuration(getDefaultMaterialDuration(resolvedStudent?.profile?.class))
        }
    }, [visible, sourceLevel, resolvedStudent?.profile?.class])

    const levelOptions = Object.keys(materialLevelsColors).map((x) => ({
        label: materialLevelsNames?.[x],
        value: x,
        leftComponent: (
            <IconImageM
                source={materialLevelsIcons[x]}
                width={18}
                style={{
                    tintColor: materialLevelsColors[x],
                    marginRight: 10,
                }}
            />
        ),
    }))

    const handleDismiss = () => {
        onDismiss()
        setSimilarPersonalize(false)
        setSimilarInstructions("")
    }

    const handleGenerate = () => {
        onDismiss()
        setSimilarOpen(true)
    }

    const handleCloseGenerate = () => {
        setSimilarOpen(false)
        setSimilarPersonalize(false)
        setSimilarInstructions("")
    }

    return (
        <>
            <ModalM
                visible={visible}
                onDismiss={handleDismiss}
                style={{
                    maxWidth: 560,
                    width: "92%",
                }}
            >
                <View style={{ padding: 20 }}>
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginBottom: 20,
                        }}
                    >
                        <IconButton
                            icon="arrow-left"
                            iconColor={theme.colors.primary}
                            size={20}
                            onPress={handleDismiss}
                            style={{ margin: 0, marginLeft: -8 }}
                        />
                        <TextM
                            fontSize={16}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            style={{ flex: 1 }}
                            numberOfLines={1}
                        >
                            {sourceTitle
                                ? `Générer à partir de l'exo « ${truncateSourceTitle(sourceTitle)} »`
                                : "Autre exercice"}
                        </TextM>
                    </View>

                    {sourceUnreliable && (
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "flex-start",
                                backgroundColor: theme.colors.warningLight,
                                borderRadius: 10,
                                paddingVertical: 10,
                                paddingHorizontal: 12,
                                marginBottom: 12,
                            }}
                        >
                            <View
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 10,
                                    backgroundColor: theme.colors.warning,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: 10,
                                    marginTop: 1,
                                }}
                            >
                                <FontAwesome5
                                    name="exclamation"
                                    size={10}
                                    color={theme.colors.white}
                                />
                            </View>
                            <View style={{ flex: 1 }}>
                                <TextM fontSize={13} fontWeight="Bold" color={theme.colors.blackLight} wrap>
                                    {SOURCE_CHAPTER_FIX_TEXTS.title}
                                </TextM>
                                <TextM fontSize={12} color={theme.colors.grey} wrap style={{ marginTop: 2 }}>
                                    {canFixChapter
                                        ? sourceChapterFixNotice(fix.status, fixScope)
                                        : SOURCE_CHAPTER_FIX_TEXTS.waiting}
                                </TextM>
                                {fix.status === "failed" && (
                                    <TouchableOpacity onPress={fix.retry} style={{ marginTop: 4 }}>
                                        <TextM fontSize={12} fontWeight="Bold" color={theme.colors.primary}>
                                            {SOURCE_CHAPTER_FIX_TEXTS.retry}
                                        </TextM>
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>
                    )}
                    {/* The lock is the correction: the chapters of the material's
                        subject in the student's class, picked here. */}
                    {fix.shown && fix.status !== "empty" && fix.status !== "failed" && (
                        <View style={{ marginBottom: 16, zIndex: 1000 }}>
                            <ChapterSearchDropdown
                                label="Chapitre"
                                chapters={fix.chapters}
                                selectedChapterId={fix.chapterId != null ? String(fix.chapterId) : null}
                                onSelectChapter={(chapterId) => {
                                    if (chapterId != null) fix.pick(chapterId)
                                }}
                                loading={fix.status === "loading" || fix.saving}
                                disabled={fix.saving}
                                placeholder="Rechercher un chapitre..."
                            />
                            {!sourceUnreliable && fix.chapterId != null && (
                                <TextM fontSize={12} color={theme.colors.success} wrap style={{ marginTop: 6 }}>
                                    {SOURCE_CHAPTER_FIX_TEXTS.saved}
                                </TextM>
                            )}
                        </View>
                    )}
                    {fix.error != null && (
                        <TextM fontSize={12} color={theme.colors.error} wrap style={{ marginBottom: 12 }}>
                            {fix.error}
                        </TextM>
                    )}

                    {/* Type and Durée selectors */}
                    <View style={{ flexDirection: "row", marginBottom: 20 }}>
                        <View style={{ flex: 1, marginRight: 10 }}>
                            <SelectInputM
                                label="Type"
                                options={levelOptions}
                                value={selectedLevel}
                                onSelect={setSelectedLevel}
                            />
                        </View>
                        <View style={{ flex: 1, marginLeft: 10 }}>
                            <SelectInputM
                                label="Durée"
                                options={MATERIAL_DURATION_OPTIONS}
                                value={selectedDuration}
                                onSelect={setSelectedDuration}
                            />
                        </View>
                    </View>

                    {/* Instructions field with Personnaliser button */}
                    <View style={{ marginBottom: 20 }}>
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 5,
                            }}
                        >
                            <TextM
                                color={theme.colors.primary}
                                fontWeight="Medium"
                                fontSize={12}
                            >
                                Instructions (optionnel)
                            </TextM>
                            {resolvedStudent?.profile?.interests && (
                                <Tooltip title="Intègre son sport préféré dans les énoncés">
                                    <TouchableOpacity
                                        onPress={() =>
                                            setSimilarPersonalize(
                                                !similarPersonalize
                                            )
                                        }
                                        activeOpacity={0.7}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            paddingVertical: 5,
                                            paddingHorizontal: 10,
                                            borderRadius: 14,
                                            backgroundColor: similarPersonalize
                                                ? theme.colors.primary
                                                : theme.colors.lighter,
                                            borderWidth: 1,
                                            borderColor: similarPersonalize
                                                ? theme.colors.primary
                                                : theme.colors.lightMore,
                                        }}
                                    >
                                        <FontAwesome5
                                            name={
                                                similarPersonalize
                                                    ? "futbol"
                                                    : "magic"
                                            }
                                            size={11}
                                            color={
                                                similarPersonalize
                                                    ? theme.colors.white
                                                    : theme.colors.grey
                                            }
                                            style={{ marginRight: 6 }}
                                        />
                                        <TextM
                                            color={
                                                similarPersonalize
                                                    ? theme.colors.white
                                                    : theme.colors.grey
                                            }
                                            fontSize={11}
                                            fontWeight="Medium"
                                        >
                                            {similarPersonalize
                                                ? resolvedStudent?.profile?.interests
                                                : "Personnaliser"}
                                        </TextM>
                                        {similarPersonalize && (
                                            <FontAwesome5
                                                name="times"
                                                size={10}
                                                color={theme.colors.white}
                                                style={{ marginLeft: 6 }}
                                            />
                                        )}
                                    </TouchableOpacity>
                                </Tooltip>
                            )}
                        </View>
                        <UncontrolledTextInputM
                            value={similarInstructions}
                            onChangeText={setSimilarInstructions}
                            multiline
                            numberOfLines={2}
                            placeholder="Décris l'activité que tu souhaites générer..."
                            mode="outlined"
                            outlineColor={theme.colors.lightMore}
                            activeOutlineColor={theme.colors.primary}
                            style={{
                                backgroundColor: theme.colors.white,
                                minHeight: 80,
                            }}
                        />
                    </View>

                    <FilledButtonM
                        label={
                            !sourceUnreliable
                                ? "Générer"
                                : !canFixChapter
                                  ? SOURCE_CHAPTER_FIX_TEXTS.waitingLabel
                                  : fixWithoutChapter
                                    ? SOURCE_CHAPTER_FIX_TEXTS.withoutChapter
                                    : "Générer"
                        }
                        icon={sourceUnreliable && !canFixChapter ? "lock" : "magic"}
                        onPress={
                            fixWithoutChapter
                                ? () => fix.confirmWithoutChapter(handleGenerate)
                                : handleGenerate
                        }
                        disabled={generateLocked}
                        loading={fix.saving && fixWithoutChapter}
                        color={generateLocked ? theme.colors.light : undefined}
                        wrapText
                    />
                </View>
            </ModalM>
            {isSimilarOpen && (
                <GenerateActivityModal
                    isOpen={isSimilarOpen}
                    onClose={handleCloseGenerate}
                    student={resolvedStudent}
                    schoolSubjects={schoolSubjects}
                    mode="similar"
                    selectedLevel={selectedLevel}
                    selectedDuration={selectedDuration}
                    initialStep="results"
                    materialId={materialId}
                    apiRequest={apiRequest}
                    isLiveMode={isLiveMode}
                    nav={navigation}
                    similarInstructions={similarInstructions || null}
                    similarStudentInterests={
                        similarPersonalize
                            ? resolvedStudent?.profile?.interests
                            : null
                    }
                />
            )}
        </>
    )
}

export default ParcoursChoiceModal
